import React, { FC, ReactElement, useContext } from 'react'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { LanguageContext } from '../../Common/Contexts'
import { TelSysConfiguration } from '../../../../i18n'
import ModalExitPromptContext, { ModalExitPromptType } from '../../Common/Components/Modal/ModalExitPromptContext'
import { CommonFormContext, FormContextType } from '../../Common/Contexts/CommonFormContext'
import { openHelpPortalByTopic } from '../../Common/Utils'
import { servletPath } from '../../../NonReact/common/RxJS/RxJS'
import { DocTopic } from '../../Common/Constants/Documentation'
import Icon, { IconName } from '../../Common/Components/Icon'
import { IconSize } from '../../../NonReact/constants/React/Icon/Constants'
import Heading, { HeadingLevel } from '../../Common/Components/Heading'
import P from '../../Common/Components/P'
import { HelpTag } from '../../Common/Components/HelpTag/HelpTag'
import { HelpTagTitles, Tags } from '../../Common/API/Contentful/Constants'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { ButtonSplitIcon } from '../../Common/Components/ButtonSplitIcon/ButtonSplitIcon'
import { IsStringEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { PopoverItems } from '../../Common/Components/ButtonSplitIcon/Types'
import { AppDownloads } from '../Types/Types'
import { RoundedButton } from '../../../ReactRouting/Components/Atoms/RoundedButton/RoundedButton'
import { EMPTY_STRING } from '../../Common/Constants/Separators'

type Props = {
    serverError?: Nullable<string>
    telSysType: TelSysTypes
    closeModal: () => void
    appDownloads: AppDownloads
    /** Whether CX Integration applies to the telsys (CME capability + Teams business-option rule). */
    isCxiAvailable: boolean
}

export const FooterActions: FC<Props> = ({
    serverError,
    telSysType,
    closeModal,
    appDownloads,
    isCxiAvailable
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler

    const { displayExitPrompt }: ModalExitPromptType = useContext(ModalExitPromptContext)
    const commonForm: Nullable<FormContextType> = useContext(CommonFormContext)

    const handleHelpClick = (): void => {
        openHelpPortalByTopic(servletPath, DocTopic.ADMINTELSERVERSPROPS)
    }

    const handleCancel = (): void => {
        if (commonForm?.isTouched) {
            displayExitPrompt()
        } else {
            closeModal()
        }
    }

    const appDownloadItems: PopoverItems[] = [
        {
            label: language.get(TelSysConfiguration.TelSysConfiguration_FooterActions_CXA_Native_Teams_App_URL),
            href: appDownloads?.CXANativeTeamsAppURL,
            isDisabled: IsStringEmpty(appDownloads?.CXANativeTeamsAppURL)
        },
        {
            label: language.get(TelSysConfiguration.TelSysConfiguration_FooterActions_CXI_Client_Windows_URL),
            href: appDownloads?.CXIClientWindowsURL,
            isDisabled: IsStringEmpty(appDownloads?.CXIClientWindowsURL)
        },
        {
            label: language.get(TelSysConfiguration.TelSysConfiguration_FooterActions_CXI_Client_Mac_URL),
            href: appDownloads?.CXIClientMacURL,
            isDisabled: IsStringEmpty(appDownloads?.CXIClientMacURL)
        },
        {
            label: language.get(TelSysConfiguration.TelSysConfiguration_FooterActions_Search_Call_Teams_App_URL),
            href: appDownloads?.SearchCallTeamsAppURL,
            isDisabled: IsStringEmpty(appDownloads?.SearchCallTeamsAppURL)
        }
    ]

    return (
        <div>
            {serverError && (
                <div className='auf-server-error'>
                    <Icon iconName={IconName.ERRORCIRCLE} iconSize={IconSize.LARGE} />
                    <div>
                        <Heading level={HeadingLevel.TWO}>
                            {language.get(TelSysConfiguration.TelSysConfiguration_Footer_ServerErrorTitle)}
                        </Heading>
                        <P>{serverError}</P>
                    </div>
                </div>
            )}
            <div className='tabs-footer'>
                {telSysType === TelSysTypes.MS_TEAMS ? (
                    <>
                        {isCxiAvailable ? (
                            <ButtonSplitIcon
                                isRouterMode
                                title={language.get(
                                    TelSysConfiguration.TelSysConfiguration_FooterActions_App_Downloads_Title
                                )}
                                items={appDownloadItems}
                            />
                        ) : (
                            <RoundedButton
                                shrink
                                isOutlined
                                download={true}
                                href={appDownloads?.CXANativeTeamsAppURL ?? EMPTY_STRING}
                            >
                                {language.get(TelSysConfiguration.TelSysConfiguration_FooterActions_App_Download_Title)}
                            </RoundedButton>
                        )}
                        <HelpTag
                            helpContentTitle={HelpTagTitles.TEAMS_APPSETUP}
                            tag={Tags.TELSYS_CONFIGURATION}
                            buttonLabel={language.get(
                                TelSysConfiguration.TelSysConfiguration_FooterActions_TeamsAppHelp
                            )}
                            isRouterMode
                        />
                    </>
                ) : (
                    <></>
                )}
                <RoundedButton isOutlined type='button' onClick={handleHelpClick} shrink>
                    {language.get(TelSysConfiguration.TelSysConfiguration_FooterActions_Help)}
                </RoundedButton>
                <RoundedButton type='submit' shrink>
                    {language.get(TelSysConfiguration.TelSysConfiguration_FooterActions_Submit)}
                </RoundedButton>
                <RoundedButton type='button' onClick={handleCancel} shrink>
                    {language.get(TelSysConfiguration.TelSysConfiguration_FooterActions_Cancel)}
                </RoundedButton>
            </div>
        </div>
    )
}
